import { useCallback, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";
import { generateRandomString } from "utils/helper";
import * as services from "../services/accountCategoryApi";
import { useTranslation } from "react-i18next";
import {
  setIsLoading,
  setAccountCategoryList,
} from "../slices/accountCategorySlice";

function toParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    pagination: {
      page: pagination.page,
      size: pagination.pageSize,
    },
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useAccountCategoryList() {
  const { t, i18n } = useTranslation();
  const [groupTypeCode, setGroupTypeCode] = useState("ACCOUNT_CATEGORY");
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.accountCategory.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const accountCategoryList = useSelector(
    (state) => state.accountCategory.accountCategoryList
  );
  const [currentAccountCategory, setCurrentAccountCategory] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);

  const initialValues = useMemo(() => {
    return {
      ...currentAccountCategory
    };
  }, [currentAccountCategory]);

  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);

  const onEdit = (record) => {
    setCurrentAccountCategory(record);
    setModalOpen(true);
  };

  const onCreate = useCallback(() => {
    setCurrentAccountCategory({});
    setModalOpen(true);
  }, []);

  const onCancel = useCallback(() => {
    setCurrentAccountCategory({});
    setModalOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    Modal.confirm({
      title: t("dialog.confirmation.header"),
      content:
        i18n.language === "en"
          ? "Are you sure you want to cancel the changes?"
          : "คุณต้องการยกเลิกการแก้ไขใช่หรือไม่ ?",
      onOk() {
        onCancel();
      },
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
    });
  }, [onCancel, t, i18n.language]);

  const getAccountCategoryList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getCommonTypeCompany({
          requestId: generateRandomString(),
          companyId: currentCompany,
          groupTypeCode: groupTypeCode,
          ...params.pagination,
          sortBy: params.sortBy,
          ...params
        });
        dispatch(setAccountCategoryList(response.data?.content || []));
        setPagination({
          ...pagination,
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );


  const handleOnChange = useCallback(
    (tablePagination, tableFilters, tableSorter, tableExtra) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      // TODO: If sorting or filtering has changed, Change page to 0
      getAccountCategoryList(modifiedParams);
    },
    [getAccountCategoryList]
  );

  const handleOnStatus = useCallback(
    async (val, record) => {
      console.log("val, record", val, record);
      try {
        dispatch(setIsLoading(true));
        const values = {
          id: record.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: val ? "ACTIVE" : "INACTIVE",
          actionId: currentEmployee,
        };
        await services.manageStatusCommonTypeCompany(values);
        const modifiedAccountCategoryList = {
          pagination: {
            ...pagination,
            page: pagination.page - 1,
          },
        };
        getAccountCategoryList(modifiedAccountCategoryList);
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch, getAccountCategoryList]
  );

  const deleteAccountCategory = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          groupTypeCode: groupTypeCode,
          id: values.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: "DELETE",
          actionId: currentEmployee,
        };
        await services.manageStatusCommonTypeCompany(modifiedValues);
        message.success(t("common.success"))

        onCancel();
        getAccountCategoryList({ pagination: { page: 0, size: 10 } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getAccountCategoryList]
  );

  const handleDelete = useCallback(
    (values) => {
      // const modifiedValues = { ...values };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {t("dialog.confirmation.delete").replace("{entity}", t("account_category_list.entity"))}
            </p>
          </>
        ),
        onOk() {
          if (values) {
            deleteAccountCategory(values)
          }
        },
        okText: t("common.confirm").toUpperCase(),
        cancelText: t("common.cancel").toUpperCase(),
      });
    },
    [currentCompany, deleteAccountCategory]
  );

  const createAccountCategory = useCallback(
    async (values = {}) => {
      try {
        const modifiedValues = {
          groupTypeCode: groupTypeCode,
          commonTypeName: values.commonTypeName,
          commonTypeNameEn: values.commonTypeNameEn,
          requestId: generateRandomString(),
          companyId: currentCompany,
          actionId: currentEmployee,
        };
        await services.createCommonTypeCompany(modifiedValues);
        dispatch(setIsLoading(true));
        message.success(t("common.success"))
        onCancel();
        getAccountCategoryList({ pagination: { page: 0, size: 10 } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getAccountCategoryList]
  );

  const updateAccountCategory
    = useCallback(
      async (values = {}) => {
        try {
          dispatch(setIsLoading(true));
          const modifiedValues = {
            groupTypeCode: groupTypeCode,
            id: values.id,
            commonTypeName: values.commonTypeName,
            commonTypeNameEn: values.commonTypeNameEn,
            requestId: generateRandomString(),
            companyId: currentCompany,
            actionId: currentEmployee,
          };
          await services.updateCommonTypeCompany(modifiedValues);
          message.success(t("common.success"))

          onCancel();
          getAccountCategoryList({ pagination: { page: 0, size: 10 } });
        } catch (error) {
          message.error(t("common.error"))
        } finally {
          dispatch(setIsLoading(false));
        }
      },
      [currentCompany, dispatch, onCancel, getAccountCategoryList]
    );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = { ...values, companyId: currentCompany };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {(values.id
                ? t("dialog.confirmation.edit")
                : t("dialog.confirmation.create")
              ).replace("{entity}", t("account_category_list.entity"))}
            </p>
          </>
        ),
        onOk() {
          if (values.id) {
            updateAccountCategory(modifiedValues);
          } else {
            createAccountCategory(modifiedValues);
          }
        },
        okText: t("common.confirm").toUpperCase(),
        cancelText: t("common.cancel").toUpperCase(),
      });
    },
    [currentCompany, updateAccountCategory, createAccountCategory]
  );

  const onSearch = useCallback(
    (values) => {
      values.page = 0;
      values.size = 10;
      getAccountCategoryList(values);
    },
    [currentCompany]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object" && currentCompany !== null) {
      getAccountCategoryList({ pagination: { page: 0, size: 10 } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);



  return {
    onSearch,
    onSubmit,
    onCreate,
    onEdit,
    onCancel: handleCancel,
    onDelete: handleDelete,
    initialValues,
    isModalOpen,
    accountCategoryList: accountCategoryList,
    isLoading,
    pagination,
    onChange: handleOnChange,
    onStatus: handleOnStatus,
    // amountIncomeDeductionOptions,
  };
}

export default useAccountCategoryList;
